import React, { useState } from "react";
import GlobalWrapper from "../../components/Wrapper/GlobalWrapper";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  IconButton,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Tabs,
  Tab,
  Badge,
  Tooltip,
  Divider,
  InputAdornment,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  Add,
  Search,
  FilterList,
  CheckCircle,
  AccessTime,
  Flag,
  Person,
  Assignment,
  TrendingUp,
  VerifiedUser,
  PlayArrow,
  Pause,
  Visibility,
  Edit,
  Delete,
  CalendarToday,
  Group,
  TaskAlt,
  PendingActions,
  Cancel,
  MoreVert,
  Close,
  AssignmentTurnedIn,
  HourglassEmpty,
  ErrorOutline,
  Star,
} from "@mui/icons-material";

// ─── Mock Data ───────────────────────────────────────────────────────────────

const employees = [
  { id: 1, name: "Aarav Sharma", avatar: "AS", role: "Frontend Developer", department: "Engineering" },
  { id: 2, name: "Priya Patel", avatar: "PP", role: "UI/UX Designer", department: "Design" },
  { id: 3, name: "Rahul Verma", avatar: "RV", role: "Backend Developer", department: "Engineering" },
  { id: 4, name: "Sneha Gupta", avatar: "SG", role: "QA Engineer", department: "Quality" },
  { id: 5, name: "Vikram Singh", avatar: "VS", role: "DevOps Engineer", department: "Infrastructure" },
];

const initialTasks = [
  { id: 1, title: "Design new dashboard layout", assignee: 2, priority: "high", status: "in-progress", dueDate: "2026-04-20", verified: false, description: "Create wireframes and mockups for the new analytics dashboard", progress: 65 },
  { id: 2, title: "Implement user authentication API", assignee: 3, priority: "critical", status: "in-progress", dueDate: "2026-04-18", verified: false, description: "Build JWT-based auth endpoints with refresh tokens", progress: 80 },
  { id: 3, title: "Write unit tests for payment module", assignee: 4, priority: "medium", status: "pending", dueDate: "2026-04-22", verified: false, description: "Cover all edge cases for payment processing", progress: 0 },
  { id: 4, title: "Setup CI/CD pipeline for staging", assignee: 5, priority: "high", status: "completed", dueDate: "2026-04-15", verified: false, description: "Configure GitHub Actions for automated deployments", progress: 100 },
  { id: 5, title: "Build responsive navbar component", assignee: 1, priority: "low", status: "completed", dueDate: "2026-04-14", verified: true, description: "Mobile-first navigation with drawer support", progress: 100 },
  { id: 6, title: "Optimize database queries", assignee: 3, priority: "high", status: "pending", dueDate: "2026-04-25", verified: false, description: "Reduce query time for reports by 50%", progress: 0 },
  { id: 7, title: "Create onboarding flow screens", assignee: 2, priority: "medium", status: "completed", dueDate: "2026-04-16", verified: false, description: "Design 5-step onboarding experience for new users", progress: 100 },
  { id: 8, title: "Fix login page responsiveness", assignee: 1, priority: "critical", status: "in-progress", dueDate: "2026-04-17", verified: false, description: "Login form breaks on tablets and small screens", progress: 40 },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const priorityConfig = {
  critical: { color: "#DC2626", bg: "rgba(220,38,38,0.08)", label: "Critical", icon: <ErrorOutline sx={{ fontSize: 16 }} /> },
  high: { color: "#F59E0B", bg: "rgba(245,158,11,0.08)", label: "High", icon: <Flag sx={{ fontSize: 16 }} /> },
  medium: { color: "#3B82F6", bg: "rgba(59,130,246,0.08)", label: "Medium", icon: <Star sx={{ fontSize: 16 }} /> },
  low: { color: "#10B981", bg: "rgba(16,185,129,0.08)", label: "Low", icon: <CheckCircle sx={{ fontSize: 16 }} /> },
};

const statusConfig = {
  pending: { color: "#F59E0B", bg: "rgba(245,158,11,0.08)", label: "Pending", icon: <HourglassEmpty sx={{ fontSize: 16 }} /> },
  "in-progress": { color: "#3B82F6", bg: "rgba(59,130,246,0.08)", label: "In Progress", icon: <PlayArrow sx={{ fontSize: 16 }} /> },
  completed: { color: "#10B981", bg: "rgba(16,185,129,0.08)", label: "Completed", icon: <TaskAlt sx={{ fontSize: 16 }} /> },
  cancelled: { color: "#EF4444", bg: "rgba(239,68,68,0.08)", label: "Cancelled", icon: <Cancel sx={{ fontSize: 16 }} /> },
};

const avatarColors = ["#6366F1", "#EC4899", "#F59E0B", "#10B981", "#3B82F6", "#8B5CF6"];

const getEmployee = (id) => employees.find((e) => e.id === id);

// ─── Stat Card ───────────────────────────────────────────────────────────────

const StatCard = ({ icon, title, value, gradient, subtitle }) => (
  <Card
    sx={{
      p: 0,
      borderRadius: 3,
      overflow: "hidden",
      border: "1px solid rgba(0,0,0,0.04)",
      transition: "all 0.3s ease",
      "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 32px rgba(0,0,0,0.08)" },
    }}
  >
    <Box sx={{ height: 4, background: gradient }} />
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="body2" sx={{ color: "#888", fontWeight: 500, mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: "#1a1a2e" }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: "#aaa", mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: 2.5,
            background: gradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ─── Main Component ──────────────────────────────────────────────────────────

const ReportingHeadView = () => {
  const [tasks, setTasks] = useState(initialTasks);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterEmployee, setFilterEmployee] = useState("all");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    assignee: "",
    priority: "medium",
    dueDate: "",
    status: "pending",
  });

  // Stats
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter((t) => t.status === "completed").length;
  const inProgressTasks = tasks.filter((t) => t.status === "in-progress").length;
  const pendingTasks = tasks.filter((t) => t.status === "pending").length;
  const verifiedTasks = tasks.filter((t) => t.verified).length;
  const overdueTasks = tasks.filter((t) => new Date(t.dueDate) < new Date() && t.status !== "completed").length;

  // Filter tasks
  const filteredTasks = tasks.filter((t) => {
    const matchesSearch = t.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPriority = filterPriority === "all" || t.priority === filterPriority;
    const matchesEmployee = filterEmployee === "all" || t.assignee === Number(filterEmployee);
    const matchesTab =
      activeTab === 0 ||
      (activeTab === 1 && t.status === "pending") ||
      (activeTab === 2 && t.status === "in-progress") ||
      (activeTab === 3 && t.status === "completed") ||
      (activeTab === 4 && t.verified);
    return matchesSearch && matchesPriority && matchesEmployee && matchesTab;
  });

  // Group tasks by employee
  const tasksByEmployee = employees.map((emp) => ({
    ...emp,
    tasks: tasks.filter((t) => t.assignee === emp.id),
    completed: tasks.filter((t) => t.assignee === emp.id && t.status === "completed").length,
    total: tasks.filter((t) => t.assignee === emp.id).length,
  }));

  // Handlers
  const handleAddTask = () => {
    if (!newTask.title || !newTask.assignee || !newTask.dueDate) return;
    const task = {
      id: tasks.length + 1,
      ...newTask,
      assignee: Number(newTask.assignee),
      verified: false,
      progress: 0,
    };
    setTasks([task, ...tasks]);
    setNewTask({ title: "", description: "", assignee: "", priority: "medium", dueDate: "", status: "pending" });
    setAddDialogOpen(false);
  };

  const handleVerifyTask = (taskId) => {
    setTasks(tasks.map((t) => (t.id === taskId ? { ...t, verified: true } : t)));
  };

  const handleStatusChange = (taskId, newStatus) => {
    setTasks(
      tasks.map((t) =>
        t.id === taskId
          ? { ...t, status: newStatus, progress: newStatus === "completed" ? 100 : newStatus === "pending" ? 0 : t.progress }
          : t
      )
    );
  };

  const handleDeleteTask = (taskId) => {
    setTasks(tasks.filter((t) => t.id !== taskId));
  };

  return (
    <>
      <GlobalWrapper>
        <Box sx={{ width: "100%", minHeight: "100vh" }}>
          {/* ── Header ─────────────────────────────────────────────── */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
              borderRadius: 4,
              p: { xs: 3, sm: 4 },
              mb: 4,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -60,
                right: -60,
                width: 200,
                height: 200,
                borderRadius: "50%",
                background: "rgba(99,102,241,0.12)",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: -40,
                left: "30%",
                width: 140,
                height: 140,
                borderRadius: "50%",
                background: "rgba(236,72,153,0.08)",
              }}
            />
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, position: "relative", zIndex: 1 }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800, color: "#fff", mb: 0.5 }}>
                  Task Management
                </Typography>
                <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.6)" }}>
                  Monitor, assign and verify tasks across your team
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setAddDialogOpen(true)}
                sx={{
                  background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                  borderRadius: 2.5,
                  px: 3,
                  py: 1.2,
                  fontWeight: 700,
                  textTransform: "none",
                  fontSize: 15,
                  boxShadow: "0 8px 24px rgba(99,102,241,0.3)",
                  "&:hover": { background: "linear-gradient(135deg, #5558E6 0%, #7C4FE0 100%)" },
                }}
              >
                Assign New Task
              </Button>
            </Box>
          </Box>

          {/* ── Stat Cards ─────────────────────────────────────────── */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard icon={<Assignment />} title="Total Tasks" value={totalTasks} gradient="linear-gradient(135deg, #6366F1, #8B5CF6)" subtitle="All assigned" />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard icon={<PendingActions />} title="Pending" value={pendingTasks} gradient="linear-gradient(135deg, #F59E0B, #F97316)" subtitle="Awaiting start" />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard icon={<TrendingUp />} title="In Progress" value={inProgressTasks} gradient="linear-gradient(135deg, #3B82F6, #2563EB)" subtitle="Being worked on" />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard icon={<TaskAlt />} title="Completed" value={completedTasks} gradient="linear-gradient(135deg, #10B981, #059669)" subtitle={`${verifiedTasks} verified`} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard icon={<ErrorOutline />} title="Overdue" value={overdueTasks} gradient="linear-gradient(135deg, #EF4444, #DC2626)" subtitle="Need attention" />
            </Grid>
          </Grid>

          {/* ── Employee Overview ──────────────────────────────────── */}
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a2e", mb: 2 }}>
            Employee Task Overview
          </Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {tasksByEmployee.map((emp, idx) => (
              <Grid item xs={12} sm={6} md={2.4} key={emp.id}>
                <Card
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    border: "1px solid rgba(0,0,0,0.04)",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": { transform: "translateY(-3px)", boxShadow: "0 8px 24px rgba(0,0,0,0.06)" },
                  }}
                  onClick={() => setFilterEmployee(String(emp.id))}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                    <Avatar sx={{ bgcolor: avatarColors[idx % avatarColors.length], width: 40, height: 40, fontSize: 14, fontWeight: 700 }}>
                      {emp.avatar}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#1a1a2e", lineHeight: 1.2 }}>
                        {emp.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#999" }}>
                        {emp.role}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: "#888" }}>
                        Progress
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "#1a1a2e" }}>
                        {emp.total > 0 ? Math.round((emp.completed / emp.total) * 100) : 0}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={emp.total > 0 ? (emp.completed / emp.total) * 100 : 0}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: "rgba(0,0,0,0.04)",
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 3,
                          background: `linear-gradient(90deg, ${avatarColors[idx % avatarColors.length]}, ${avatarColors[(idx + 1) % avatarColors.length]})`,
                        },
                      }}
                    />
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Chip label={`${emp.completed} done`} size="small" sx={{ fontSize: 11, bgcolor: "rgba(16,185,129,0.08)", color: "#10B981", fontWeight: 600 }} />
                    <Chip label={`${emp.total - emp.completed} left`} size="small" sx={{ fontSize: 11, bgcolor: "rgba(245,158,11,0.08)", color: "#F59E0B", fontWeight: 600 }} />
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* ── Filters & Tabs ─────────────────────────────────────── */}
          <Card sx={{ borderRadius: 3, mb: 3, border: "1px solid rgba(0,0,0,0.04)" }}>
            <Box sx={{ px: 3, pt: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{
                  "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: 14, minHeight: 44 },
                  "& .Mui-selected": { color: "#6366F1" },
                  "& .MuiTabs-indicator": { background: "linear-gradient(90deg, #6366F1, #8B5CF6)", height: 3, borderRadius: 2 },
                }}
              >
                <Tab label={`All (${totalTasks})`} />
                <Tab label={`Pending (${pendingTasks})`} />
                <Tab label={`In Progress (${inProgressTasks})`} />
                <Tab label={`Completed (${completedTasks})`} />
                <Tab label={`Verified (${verifiedTasks})`} />
              </Tabs>
            </Box>
            <Divider />
            <Box sx={{ p: 2, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "#bbb" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 240, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Priority</InputLabel>
                <Select value={filterPriority} label="Priority" onChange={(e) => setFilterPriority(e.target.value)} sx={{ borderRadius: 2 }}>
                  <MenuItem value="all">All Priorities</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Employee</InputLabel>
                <Select value={filterEmployee} label="Employee" onChange={(e) => setFilterEmployee(e.target.value)} sx={{ borderRadius: 2 }}>
                  <MenuItem value="all">All Employees</MenuItem>
                  {employees.map((emp) => (
                    <MenuItem key={emp.id} value={String(emp.id)}>
                      {emp.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {(filterPriority !== "all" || filterEmployee !== "all" || searchQuery) && (
                <Button
                  size="small"
                  onClick={() => {
                    setFilterPriority("all");
                    setFilterEmployee("all");
                    setSearchQuery("");
                  }}
                  sx={{ textTransform: "none", color: "#6366F1" }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          </Card>

          {/* ── Task Table ─────────────────────────────────────────── */}
          <TableContainer component={Paper} sx={{ borderRadius: 3, border: "1px solid rgba(0,0,0,0.04)", boxShadow: "none" }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "rgba(99,102,241,0.03)" }}>
                  <TableCell sx={{ fontWeight: 700, color: "#555" }}>Task</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#555" }}>Assignee</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#555" }}>Priority</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#555" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#555" }}>Due Date</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#555" }}>Progress</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#555" }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTasks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <PendingActions sx={{ fontSize: 48, color: "#ddd", mb: 1 }} />
                      <Typography variant="body1" sx={{ color: "#aaa" }}>
                        No tasks found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTasks.map((task) => {
                    const emp = getEmployee(task.assignee);
                    const priority = priorityConfig[task.priority];
                    const status = statusConfig[task.status];
                    const isOverdue = new Date(task.dueDate) < new Date() && task.status !== "completed";
                    return (
                      <TableRow
                        key={task.id}
                        sx={{
                          transition: "all 0.2s ease",
                          "&:hover": { bgcolor: "rgba(99,102,241,0.02)" },
                          opacity: task.verified ? 0.75 : 1,
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {task.verified && <VerifiedUser sx={{ fontSize: 18, color: "#10B981" }} />}
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: "#1a1a2e" }}>
                                {task.title}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#aaa" }}>
                                {task.description?.substring(0, 50)}...
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar sx={{ width: 30, height: 30, fontSize: 11, fontWeight: 700, bgcolor: avatarColors[emp?.id % avatarColors.length] }}>
                              {emp?.avatar}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {emp?.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={priority.icon}
                            label={priority.label}
                            size="small"
                            sx={{ bgcolor: priority.bg, color: priority.color, fontWeight: 600, fontSize: 12, "& .MuiChip-icon": { color: priority.color } }}
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" variant="standard">
                            <Select
                              value={task.status}
                              onChange={(e) => handleStatusChange(task.id, e.target.value)}
                              disableUnderline
                              sx={{ fontSize: 13, fontWeight: 600, color: status.color }}
                            >
                              <MenuItem value="pending">Pending</MenuItem>
                              <MenuItem value="in-progress">In Progress</MenuItem>
                              <MenuItem value="completed">Completed</MenuItem>
                              <MenuItem value="cancelled">Cancelled</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <CalendarToday sx={{ fontSize: 14, color: isOverdue ? "#EF4444" : "#bbb" }} />
                            <Typography variant="body2" sx={{ color: isOverdue ? "#EF4444" : "#666", fontWeight: isOverdue ? 700 : 400 }}>
                              {new Date(task.dueDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                            </Typography>
                            {isOverdue && <Chip label="Overdue" size="small" sx={{ ml: 0.5, height: 20, fontSize: 10, bgcolor: "rgba(239,68,68,0.08)", color: "#EF4444", fontWeight: 700 }} />}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 100 }}>
                            <LinearProgress
                              variant="determinate"
                              value={task.progress}
                              sx={{
                                flex: 1,
                                height: 6,
                                borderRadius: 3,
                                bgcolor: "rgba(0,0,0,0.04)",
                                "& .MuiLinearProgress-bar": {
                                  borderRadius: 3,
                                  background:
                                    task.progress === 100
                                      ? "linear-gradient(90deg, #10B981, #059669)"
                                      : task.progress > 50
                                      ? "linear-gradient(90deg, #3B82F6, #6366F1)"
                                      : "linear-gradient(90deg, #F59E0B, #F97316)",
                                },
                              }}
                            />
                            <Typography variant="caption" sx={{ fontWeight: 700, color: "#888", minWidth: 32 }}>
                              {task.progress}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedTask(task);
                                  setViewDialogOpen(true);
                                }}
                              >
                                <Visibility sx={{ fontSize: 18, color: "#888" }} />
                              </IconButton>
                            </Tooltip>
                            {task.status === "completed" && !task.verified && (
                              <Tooltip title="Verify Task">
                                <IconButton size="small" onClick={() => handleVerifyTask(task.id)}>
                                  <VerifiedUser sx={{ fontSize: 18, color: "#10B981" }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => handleDeleteTask(task.id)}>
                                <Delete sx={{ fontSize: 18, color: "#ccc", "&:hover": { color: "#EF4444" } }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ── Add Task Dialog ────────────────────────────────────── */}
          <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    background: "linear-gradient(135deg, #6366F1, #8B5CF6)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                  }}
                >
                  <Add sx={{ fontSize: 20 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Assign New Task
                </Typography>
              </Box>
              <IconButton onClick={() => setAddDialogOpen(false)}>
                <Close />
              </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 3 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <TextField label="Task Title" fullWidth value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Assign To</InputLabel>
                      <Select value={newTask.assignee} label="Assign To" onChange={(e) => setNewTask({ ...newTask, assignee: e.target.value })} sx={{ borderRadius: 2 }}>
                        {employees.map((emp) => (
                          <MenuItem key={emp.id} value={emp.id}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar sx={{ width: 24, height: 24, fontSize: 10, bgcolor: avatarColors[emp.id % avatarColors.length] }}>{emp.avatar}</Avatar>
                              {emp.name}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Priority</InputLabel>
                      <Select value={newTask.priority} label="Priority" onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })} sx={{ borderRadius: 2 }}>
                        <MenuItem value="critical">🔴 Critical</MenuItem>
                        <MenuItem value="high">🟡 High</MenuItem>
                        <MenuItem value="medium">🔵 Medium</MenuItem>
                        <MenuItem value="low">🟢 Low</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <TextField
                  label="Due Date"
                  type="date"
                  fullWidth
                  value={newTask.dueDate}
                  onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 1 }}>
              <Button onClick={() => setAddDialogOpen(false)} sx={{ textTransform: "none", color: "#888" }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleAddTask}
                disabled={!newTask.title || !newTask.assignee || !newTask.dueDate}
                sx={{
                  background: "linear-gradient(135deg, #6366F1, #8B5CF6)",
                  borderRadius: 2,
                  px: 3,
                  textTransform: "none",
                  fontWeight: 700,
                  "&:hover": { background: "linear-gradient(135deg, #5558E6, #7C4FE0)" },
                }}
              >
                Assign Task
              </Button>
            </DialogActions>
          </Dialog>

          {/* ── View Task Dialog ───────────────────────────────────── */}
          <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            {selectedTask && (
              <>
                <Box sx={{ height: 4, background: priorityConfig[selectedTask.priority]?.color }} />
                <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Task Details
                  </Typography>
                  <IconButton onClick={() => setViewDialogOpen(false)}>
                    <Close />
                  </IconButton>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ pt: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    {selectedTask.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#666", mb: 3, lineHeight: 1.7 }}>
                    {selectedTask.description}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: "#aaa" }}>
                        Assignee
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: avatarColors[selectedTask.assignee % avatarColors.length] }}>
                          {getEmployee(selectedTask.assignee)?.avatar}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {getEmployee(selectedTask.assignee)?.name}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: "#aaa" }}>
                        Priority
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={priorityConfig[selectedTask.priority]?.label}
                          size="small"
                          sx={{
                            bgcolor: priorityConfig[selectedTask.priority]?.bg,
                            color: priorityConfig[selectedTask.priority]?.color,
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: "#aaa" }}>
                        Status
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={statusConfig[selectedTask.status]?.label}
                          size="small"
                          sx={{
                            bgcolor: statusConfig[selectedTask.status]?.bg,
                            color: statusConfig[selectedTask.status]?.color,
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: "#aaa" }}>
                        Due Date
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                        {new Date(selectedTask.dueDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ color: "#aaa" }}>
                        Progress
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                        <LinearProgress
                          variant="determinate"
                          value={selectedTask.progress}
                          sx={{
                            flex: 1,
                            height: 8,
                            borderRadius: 4,
                            bgcolor: "rgba(0,0,0,0.04)",
                            "& .MuiLinearProgress-bar": { borderRadius: 4, background: "linear-gradient(90deg, #6366F1, #8B5CF6)" },
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {selectedTask.progress}%
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ color: "#aaa" }}>
                        Verification
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        {selectedTask.verified ? (
                          <Chip icon={<VerifiedUser sx={{ fontSize: 16 }} />} label="Verified" size="small" sx={{ bgcolor: "rgba(16,185,129,0.08)", color: "#10B981", fontWeight: 600 }} />
                        ) : selectedTask.status === "completed" ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<VerifiedUser />}
                            onClick={() => {
                              handleVerifyTask(selectedTask.id);
                              setSelectedTask({ ...selectedTask, verified: true });
                            }}
                            sx={{ textTransform: "none", borderColor: "#10B981", color: "#10B981", borderRadius: 2, "&:hover": { bgcolor: "rgba(16,185,129,0.04)" } }}
                          >
                            Verify This Task
                          </Button>
                        ) : (
                          <Typography variant="body2" sx={{ color: "#ccc" }}>
                            Complete task first to verify
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </DialogContent>
              </>
            )}
          </Dialog>
        </Box>
      </GlobalWrapper>
    </>
  );
};

export default ReportingHeadView;
